import { formatPrice } from '@ynap/intl-formatters'
import getOrderItems from './getOrderItems'
import getOrderStatus from './getOrderStatus'
import { mapOrderStatus } from '../../../utils/order-status/map-order-status'
import getFormattedDate from 'app/utils/getFormattedDate'

export default function getOrderDetails(state, props) {
    const { skeletonLineItems = 3 } = props
    const {
        orderDetails,
        isFetchingOrderDetails,
        fetchOrderDetailsError,
        locale,
        returnOrder,
        returnOrderError,
        returnLabel,
        user,
        isFetchingContent,
        config,
    } = state
    const { externalOrderId, messages } = props
    const {
        itemsNumber = '0',
        placedDate,
        totalProductPrice,
        derivedFromOrderNo,
        orderType,
        totalShippingCharge,
        grandTotal,
        shippingAddress,
        shippingMethodName,
        paymentInstruction,
        totalSalesTax,
        shippingDuties,
        trackingURL,
        dateShipped,
        isReturnable,
        returnSuccess,
        shipments,
        refundPaymentMethods,
        isLegacyOrder,
        exchanges,
        returns,
        orderStatus,
        courierTrackingId,
        orderItem,
        customerEmailId,
    } = orderDetails

    const orderItems = getOrderItems(state, props, isLegacyOrder)
    const skeletonOrderItems = new Array(skeletonLineItems).fill(undefined, 0, skeletonLineItems)
    const includesReturn = returnOrder ? returnOrder.returnLines.find((line) => !line.exchangePartnumber) : false
    const displayId =
        config?.head?.flags?.showExchangeDerivedOrderIds &&
        orderType?.toLowerCase() === 'exchange' &&
        derivedFromOrderNo
            ? derivedFromOrderNo
            : externalOrderId

    const returnItems =
        orderItems && returnOrder
            ? returnOrder.returnLines.map((line) => {
                  return orderItems.find((item) => {
                      return item.primeLineNo === line.lineNumber
                  })
              })
            : undefined

    const finalDateShipped = dateShipped || shipments?.[0]?.timeShipped || undefined

    let isExchangable = false

    orderItems?.find((item) => {
        return (isExchangable = isExchangable === false && item.exchangable)
    })

    const activeOrderStatus = mapOrderStatus(orderStatus)
    const numberOfReturnedItems = orderItems?.filter((i) => i.isReturn).length
    const numberOfRefusedItems = orderItems?.filter((i) => i.isReturnRefused).length
    const isPartialReturnRefused = numberOfRefusedItems > 0 && numberOfReturnedItems !== numberOfRefusedItems

    return {
        shipmentsWithItemDetails: getShipmentsWithItemDetails(
            shipments,
            orderItems,
            dateShipped,
            locale
        ),
        locale,
        orderStatus: getOrderStatus(orderStatus, messages).status,
        activeOrderStatus,
        fetchOrderDetailsError,
        isFetchingOrderDetails,
        orderItemTotal: itemsNumber,
        orderItems: orderItems ? orderItems : skeletonOrderItems,
        isReturnable,
        isLegacyOrder,
        orderDateRaw: placedDate,
        orderDate: placedDate ? getFormattedDate(placedDate, locale.id) : undefined,
        shippingAddress: shippingAddress ? { ...shippingAddress } : undefined,
        customerEmailId,
        shippingMethod: shippingMethodName
            ? [shippingMethodName]
            : [...new Set(orderItems?.map((item) => item.shippingMethodName))],
        timeDelivered:
            shipments && shipments.length > 0 && shipments[0].timeDelivered
                ? getFormattedDate(shipments[0].timeDelivered, locale.id)
                : undefined,
        timeDeliveredRaw: shipments?.[0]?.timeDelivered,
        paymentMethod: paymentInstruction
            ? paymentInstruction.map((paymentInstructionItem) => {
                  return {
                      id: paymentInstructionItem.piId,
                      type: paymentInstructionItem.payMethodId,
                      subType: paymentInstructionItem.protocolData?.[2]?.value,
                      cardNumber: paymentInstructionItem.protocolData?.[0]?.value,
                      total: formatPrice(
                          paymentInstructionItem.piAmount.value.amount / paymentInstructionItem.piAmount.value.divisor,
                          paymentInstructionItem.piAmount.currency.label,
                          locale.id
                      ),
                  }
              })
            : undefined,
        totals: grandTotal
            ? {
                  selectedShippingOption: shippingMethodName,
                  itemTotal: formatPrice(
                      totalProductPrice.value.amount / totalProductPrice.value.divisor,
                      totalProductPrice.currency.label,
                      locale.id
                  ),
                  finalShippingPrice: messages.totals.shippingPrice({
                      isFree: totalShippingCharge.value.amount === 0 ? true : false,
                      price: formatPrice(
                          totalShippingCharge.value.amount / totalShippingCharge.value.divisor,
                          totalShippingCharge.currency.label,
                          locale.id
                      ),
                  }),
                  total: formatPrice(
                      grandTotal.value.amount / grandTotal.value.divisor,
                      grandTotal.currency.label,
                      locale.id
                  ),
                  currency: grandTotal.currency.label,
                  tax:
                      totalSalesTax.value.amount > 0
                          ? formatPrice(
                                totalSalesTax.value.amount / totalSalesTax.value.divisor,
                                totalSalesTax.currency.label,
                                locale.id
                            )
                          : undefined,
                  duty:
                      shippingDuties.value.amount > 0
                          ? formatPrice(
                                shippingDuties.value.amount / shippingDuties.value.divisor,
                                shippingDuties.currency.label,
                                locale.id
                            )
                          : undefined,
              }
            : undefined,
        trackingUrl: trackingURL,
        returnTrackingUrl: shipments?.find((shipment) => shipment.returnTrackingUrl)?.returnTrackingUrl,
        shortDateShipped: finalDateShipped
            ? getFormattedDate(finalDateShipped.replace(/T.+/, ''), locale.id, 'short')
            : undefined,
        dateShipped: finalDateShipped
            ? getFormattedDate(finalDateShipped.replace(/T.+/, ''), locale.id)
            : undefined,
        dateShippedRaw: finalDateShipped?.replace(/T.+/, ''),
        returnOrder: returnOrder,
        includesReturn,
        returnSuccess,
        returnOrderError,
        returnLabelSuccess: returnLabel?.success,
        returnLabelError: returnLabel?.error,
        isFetchingReturnLabel: returnLabel?.loading,
        userTitle: user ? user.personTitle : ' ',
        userLastName: user ? user.lastName : ' ',
        email: user ? user.logonId : ' ',
        returnItems,
        helpPage: config && config.help ? config.help.returns : ' ',
        isFetchingContent,
        refundPaymentMethods,
        returnsCountry: shippingAddress?.country,
        isExchangable,
        orderType,
        displayId,
        exchanges: exchanges ? exchanges : [],
        returns: returns ? returns : [],
        courierTrackingId,
        returnCourierTrackingId: orderItem?.[0]?.extnReturnTrackingNo,
        isOrderCancelled: orderStatus === 'Cancelled',
        isReturnRefused: orderStatus === 'Return Refused' || orderStatus === 'Return Reshipped',
        isPartialReturn: orderStatus === 'Partially Return Refunded',
        isPartialReturnRefused,
        isOrderDetailsFetched: !!(placedDate || dateShipped),
    }
}

export const getShipmentsWithItemDetails = (
    shipments,
    orderItems,
    dateShipped,
    locale
) => {
    let shipmentsWithItemDetails = []
    const noShipmentItems = {
        shipmentItemDetails: [],
    }
    const groupedPrimeLineNo = []
    if (shipments && shipments.length > 0) {
        shipmentsWithItemDetails = shipments.map((shipment) => {
            const shipmentItemDetails = orderItems.filter((orderItem) =>
                shipment.shipmentItems?.find((shipmentItem) => {
                    if (
                        shipmentItem.primeLineNo == orderItem.primeLineNo &&
                        !groupedPrimeLineNo.includes(shipmentItem.primeLineNo)
                    ) {
                        groupedPrimeLineNo.push(shipmentItem.primeLineNo)
                        return true
                    }
                    //to keep cancelled orders in so they are NOT put in 'Processing' group
                    if (
                        orderItem?.orderItemStatusEN?.toLowerCase() === 'cancelled' &&
                        !groupedPrimeLineNo.includes(orderItem.primeLineNo)
                    ) {
                        groupedPrimeLineNo.push(orderItem.primeLineNo)
                        return true
                    }
                    return false
                })
            )
            const finalDateShipped = shipment.timeShipped || undefined
            const finalDateDelivered = shipment.timeDelivered || undefined
            const isReturnOrExchangeShipment = shipmentItemDetails[0]?.orderItemStatusEN === 'Return Form Created'
            const shippingMethodName = shipmentItemDetails[0]?.shippingMethodName || undefined // All items from a single shipment have the same shipping method
            return {
                ...shipment,
                shipmentItemDetails,
                shippingMethodName: shippingMethodName,
                shortDateDelivered: finalDateDelivered
                    ? getFormattedDate(finalDateDelivered.replace(/T.+/, ''), locale.id, 'short')
                    : undefined,
                dateDelivered: finalDateDelivered
                    ? getFormattedDate(finalDateDelivered.replace(/T.+/, ''), locale.id)
                    : undefined,
                dateDeliveredRaw: finalDateDelivered?.replace(/T.+/, ''),
                shortDateShipped: finalDateShipped
                    ? getFormattedDate(finalDateShipped.replace(/T.+/, ''), locale.id, 'short')
                    : undefined,
                dateShipped: finalDateShipped
                    ? getFormattedDate(finalDateShipped.replace(/T.+/, ''), locale.id)
                    : undefined,
                dateShippedRaw: finalDateShipped?.replace(/T.+/, ''),
                isReturnOrExchangeShipment,
            }
        })

        //group the rest not shipped order items
        if (groupedPrimeLineNo.length !== orderItems.length) {
            noShipmentItems.shipmentItemDetails = orderItems.filter(
                (item) => !groupedPrimeLineNo.includes(item.primeLineNo)
            )

            noShipmentItems.processing = true
            shipmentsWithItemDetails.push(noShipmentItems)
        }
    } else {
        const finalDateShipped = dateShipped || undefined
        shipmentsWithItemDetails = [
            {
                shipmentItemDetails: orderItems,
                shortDateShipped: finalDateShipped
                    ? getFormattedDate(finalDateShipped.replace(/T.+/, ''), locale.id, 'short')
                    : undefined,
                dateShipped: finalDateShipped
                    ? getFormattedDate(finalDateShipped.replace(/T.+/, ''), locale.id)
                    : undefined,
                dateShippedRaw: finalDateShipped?.replace(/T.+/, ''),
            },
        ]
    }
    return shipmentsWithItemDetails
}
